<template>
  <div class="min-h-screen">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="home" />

    <!-- Main content: 2 columns -->
    <div class="max-w-6xl mx-auto px-4 py-6">
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">

        <!-- Left column: News (3/5) -->
        <div class="lg:col-span-3">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold text-gray-900">{{ t('home.latest_news') }}</h2>
            <a :href="'/news'" class="text-sm text-blue-700 hover:underline">{{ t('home.all_news') }} &rarr;</a>
          </div>

          <div v-if="newsItems.length === 0" class="text-sm text-gray-400 py-8 text-center">{{ t('home.no_news') }}</div>

          <div class="space-y-4">
            <a v-for="item in newsItems" :key="item.id" :href="`/news/${item.id}`"
               class="block bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition overflow-hidden">
              <img v-if="item.cover_image_url" :src="coverUrl(item)" class="w-full" />
              <div class="p-4">
                <h3 class="text-base font-semibold text-gray-900 hover:text-blue-700 mb-1">{{ item.title }}</h3>
                <p class="text-xs text-gray-500 mb-2">{{ item.user.name }} · {{ formatDate(item.created_at) }}</p>
                <p class="text-sm text-gray-600">{{ stripHtml(item.body).substring(0, 150) }}{{ stripHtml(item.body).length > 150 ? '...' : '' }}</p>
                <div class="flex items-center gap-3 mt-2">
                  <span class="text-xs text-gray-400">{{ item.comments_count }} {{ t('home.comments') }}</span>
                  <template v-if="userAdmin">
                    <a :href="`/news/${item.id}/edit`" @click.stop class="text-xs text-blue-700 hover:underline">{{ t('news.edit') }}</a>
                    <button @click.stop.prevent="deleteNewsItem(item)" class="text-xs text-red-500 hover:underline">{{ t('news.delete') }}</button>
                  </template>
                </div>
              </div>
            </a>
          </div>
        </div>

        <!-- Right column (2/5): split vertically -->
        <div class="lg:col-span-2 flex flex-col gap-6">

          <!-- Top forum topics -->
          <div>
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-gray-900">{{ t('home.hot_topics') }}</h2>
              <a :href="'/forum'" class="text-sm text-blue-700 hover:underline">{{ t('home.all_forum') }} &rarr;</a>
            </div>

            <div v-if="topTopics.length === 0" class="text-sm text-gray-400 py-4 text-center bg-white rounded-xl border border-gray-200">{{ t('home.no_topics') }}</div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
              <a v-for="topic in topTopics" :key="topic.id" :href="`/forum/${topic.id}`"
                 class="block px-4 py-3 hover:bg-gray-50 transition">
                <h4 class="text-sm font-medium text-gray-900 hover:text-blue-700">{{ topic.title }}</h4>
                <p class="text-xs text-gray-500 mt-1">
                  {{ topic.user.name }} · {{ topic.messages_count }} {{ t('home.new_messages') }} · {{ topic.total_messages }} {{ t('home.total') }}
                </p>
              </a>
            </div>
          </div>

          <hr class="border-gray-200" />

          <!-- Today's shared runs -->
          <div>
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-gray-900">{{ t('home.today_runs') }}</h2>
              <a :href="'/shared_runs'" class="text-sm text-blue-700 hover:underline">{{ t('home.all_runs') }} &rarr;</a>
            </div>

            <div v-if="todayRuns.length === 0" class="text-sm text-gray-400 py-4 text-center bg-white rounded-xl border border-gray-200">{{ t('home.no_runs_today') }}</div>

            <div class="space-y-3">
              <a v-for="run in todayRuns" :key="run.id" :href="`/shared_runs/${run.id}`"
                 class="block bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
                <div class="flex items-center gap-2 mb-1">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ formatTime(run.date) }}
                  </span>
                  <span v-if="run.distance" class="text-xs text-gray-500">{{ run.distance }} {{ t('home.km') }}</span>
                  <span v-if="run.pace" class="text-xs text-gray-500">{{ run.pace }} {{ t('home.min_km') }}</span>
                </div>
                <p class="text-sm text-gray-700">{{ run.organizer.name }}</p>
                <p class="text-xs text-gray-400">{{ run.participants_count }} {{ t('home.participants') }}</p>
              </a>
            </div>
          </div>

          <hr class="border-gray-200" />

          <!-- Upcoming competitions -->
          <div v-if="upcomingCompetitions.length > 0">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-gray-900">{{ t('home.upcoming_competitions') }}</h2>
              <a href="/competitions" class="text-sm text-blue-700 hover:underline">{{ t('home.all_competitions') }} &rarr;</a>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <a v-for="comp in upcomingCompetitions" :key="comp.id" :href="`/competitions/${comp.id}`"
                 class="block bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition text-center">
                <img v-if="comp.logo_url" :src="comp.logo_url" class="h-20 mx-auto rounded-lg object-contain border border-gray-200 mb-2" />
                <div v-else class="h-20 mx-auto flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 mb-2">
                  <svg class="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                </div>
                <p class="text-xs font-medium text-blue-700 mb-0.5">{{ formatCompDate(comp.date) }}</p>
                <p class="text-sm font-semibold text-gray-900 leading-tight">{{ comp.name }}</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="py-6 text-center text-gray-500 text-sm">
      <p>&copy; 2026 Alerg.Eu. {{ t('footer.rights') }}</p>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import { croppedCoverUrl } from './coverCrop.js'

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  userAdmin: Boolean,
  news: Array,
  topTopics: Array,
  todayRuns: Array,
  upcomingCompetitions: Array,
  csrfToken: String
})

const { t, locale } = useI18n()
const newsItems = ref([...props.news])

async function deleteNewsItem(item) {
  if (!confirm(t('news.confirm_delete_message'))) return
  try {
    const res = await fetch(`/news/${item.id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) newsItems.value = newsItems.value.filter(n => n.id !== item.id)
  } catch {}
}

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) }
function stripHtml(html) { const tmp = document.createElement('div'); tmp.innerHTML = html; return tmp.textContent || tmp.innerText || '' }
function formatTime(d) { return new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }
function coverUrl(item) { return croppedCoverUrl(item.cover_image_url, item.cover_position) }
function formatCompDate(d) { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }
</script>
